import React, { useState, useMemo, useEffect } from 'react';
import { useHoldRequests } from '@/hooks/useHoldRequests';
import { useAuthContext } from '@/hooks/useAuthContext';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorMessage from '@/components/common/ErrorMessage';
import { Card } from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Input from '@/components/ui/Input';
import { formatLocalDate, getStatusColor, formatStatus } from '@/lib/utils';
import { Search, Inbox } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { HoldRequest } from '@/types';

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'partial_response_received', label: 'Partial Response' },
  { value: 'request_completed_active', label: 'Completed Active' },
  { value: 'request_expired', label: 'Expired' },
  { value: 'needs_review', label: 'Needs Review' },
];

const workflowStatusOptions = [
  { value: 'all', label: 'All Workflow Statuses' },
  { value: 'new_submitted', label: 'New Submitted' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending_response', label: 'Pending Response' },
  { value: 'completed', label: 'Completed' },
  { value: 'reopened', label: 'Reopened' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'exception', label: 'Exception' },
  // NOTE: partial_response_received is a derivedStatus value, NOT a workflowStatus value.
  // It was removed from this list — sending workflowStatus=partial_response_received to
  // the API returns zero results because no hold has workflow_status='partial_response_received'.
  // Use the "Status" (derivedStatus) filter to filter by partial response.
];

export default function MyRequests() {
  const { user } = useAuthContext();
  const [statusFilter, setStatusFilter] = useState('all');
  const [workflowStatusFilter, setWorkflowStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Build query params to filter by current user
  const queryParams: Record<string, string | number> = {};
  
  // Admin and AdOps Leadership can see all requests (read-only), others see their own
  if (user?.role !== 'admin' && user?.role !== 'adops_leadership' && user?.id) {
    queryParams.salesAeId = user.id;
  }
  
  queryParams.limit = 100; // Get more results for My Requests page
  
  if (workflowStatusFilter !== 'all') {
    queryParams.workflowStatus = workflowStatusFilter;
  }
  
  const queryString = new URLSearchParams(
    Object.entries(queryParams).map(([key, value]) => [key, String(value)])
  ).toString();
  
  const { data: response, isLoading, error } = useHoldRequests(queryString);

  // OPE-386: when the workflow filter is set to 'cancelled', derivedStatus is
  // frozen and meaningless as a secondary filter — reset statusFilter to 'all'
  // so the UI doesn't show an active status filter that is silently bypassed.
  useEffect(() => {
    if (workflowStatusFilter === 'cancelled' && statusFilter !== 'all') {
      setStatusFilter('all');
    }
  }, [workflowStatusFilter, statusFilter]); // reset whenever either filter changes

  // Extract requests array from API response structure
  const allRequests = response?.holdRequests || [];
  
  // Ensure allRequests is an array
  const requestsArray = Array.isArray(allRequests) ? allRequests : [];
  
  // Filter requests by status and search query (salesAeId filtering is done by API)
  const filteredRequests = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return requestsArray
      .filter((request: HoldRequest) => {
        if (statusFilter === 'all') return true;
        // OPE-386: when the workflow filter is 'cancelled', the API already returns
        // only cancelled holds AND the useEffect above resets statusFilter to 'all'.
        // The guard below is a belt-and-suspenders fallback: even if statusFilter
        // were non-'all', cancelled holds have a frozen/stale derivedStatus and must
        // not be matched against it — they always render as 'Cancelled'.
        if (workflowStatusFilter === 'cancelled') return true;
        if (request.workflowStatus === 'cancelled') return false;
        return request.derivedStatus === statusFilter;
      })
      .filter((request: HoldRequest) => {
        if (!query) return true;
        return (
          request.requestNumber?.toLowerCase().includes(query) ||
          request.advertiser?.toLowerCase().includes(query)
        );
      });
  }, [requestsArray, statusFilter, workflowStatusFilter, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message="Failed to load your requests" />;
  }


  // OPE-386 (c): route through the shared formatStatus helper — same rule,
  // one definition, no local copy that can drift from the other badge sites.
  const getStatusLabel = (derivedStatus: string, workflowStatus?: string) =>
    formatStatus(derivedStatus, workflowStatus);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            {user?.role === 'admin' || user?.role === 'adops_leadership' ? 'All Requests' : 'My Requests'}
          </h1>
          <p className="text-neutral-600 mt-1">
            {user?.role === 'admin'
              ? 'View all hold requests (read-only access)'
              : user?.role === 'adops_leadership'
              ? 'View and manage all hold requests'
              : 'View and manage your hold requests'
            }
          </p>
        </div>
        {user?.role !== 'admin' && (
          <Link to="/holds/create" className="self-start sm:self-auto">
            <Button>Create New Request</Button>
          </Link>
        )}
      </div>

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            placeholder="Search by request number or advertiser..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
          <div className="flex items-center gap-2">
            <label htmlFor="status-filter" className="text-sm font-medium text-neutral-600 shrink-0">
              Status:
            </label>
            <Select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48"
              options={statusOptions}
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="workflow-status-filter" className="text-sm font-medium text-neutral-600 shrink-0">
              Workflow:
            </label>
            <Select
              id="workflow-status-filter"
              value={workflowStatusFilter}
              onChange={(e) => setWorkflowStatusFilter(e.target.value)}
              className="w-full sm:w-52"
              options={workflowStatusOptions}
            />
          </div>
          <div className="text-sm text-neutral-400">
            {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <Card className="text-center py-12">
          <div className="text-neutral-600 flex flex-col items-center">
            <Inbox className="h-12 w-12 text-neutral-200 mb-4" />
            {statusFilter === 'all' && !searchQuery ? (
              <>
                <p className="text-lg font-medium mb-1 text-neutral-900">No requests found</p>
                <p className="mb-4">Get started by creating your first hold request.</p>
                <Link to="/holds/create">
                  <Button>Create Request</Button>
                </Link>
              </>
            ) : (
              <>
                <p className="text-lg font-medium mb-1 text-neutral-900">
                  {searchQuery ? 'No matching requests' : `No ${statusFilter.replace('_', ' ')} requests`}
                </p>
                <p>Try {searchQuery ? 'a different search term or ' : ''}changing the filters to see more requests.</p>
              </>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request: HoldRequest) => (
            <Card key={request.id} className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Link
                      to={`/holds/${request.id}`}
                      className="text-lg font-semibold text-neutral-900 hover:text-outfront-purple"
                    >
                      {request.requestNumber}
                    </Link>
                    <Badge
                      className={getStatusColor(request.derivedStatus, request.workflowStatus)}
                    >
                      {getStatusLabel(request.derivedStatus, request.workflowStatus)}
                    </Badge>
                    {request.isAutoPerm && (
                      <Badge className="bg-outfront-purple-light text-outfront-purple">
                        Auto-Perm
                      </Badge>
                    )}
                    {request.workflowStatus && request.workflowStatus !== 'new_submitted' && request.workflowStatus !== 'cancelled' && (
                      <Badge className="bg-neutral-100 text-neutral-600">
                        {request.workflowStatus.replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                    )}
                    {request.rejectedLineItemCount != null && request.rejectedLineItemCount > 0 && (
                      <Badge className="bg-red-100 text-red-800">
                        Rejected
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 text-sm text-neutral-600">
                    <div>
                      <span className="font-medium">Advertiser:</span> {request.advertiser}
                    </div>
                    <div>
                      <span className="font-medium">Category:</span> {request.businessCategory}
                    </div>
                    <div>
                      <span className="font-medium">Region:</span> {request.salesAe?.primaryRegion || 'N/A'}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 text-sm text-neutral-600 mt-2">
                    <div>
                      <span className="font-medium">Created:</span> {formatLocalDate(request.createdAt)}
                    </div>
                    <div>
                      <span className="font-medium">Updated:</span> {formatLocalDate(request.updatedAt)}
                    </div>
                    <div>
                      <span className="font-medium">Line Items:</span> {request.totalLineItems || 0}
                    </div>
                  </div>

                </div>

                <div className="mt-3 sm:mt-0 sm:ml-4 shrink-0">
                  <Link to={`/holds/${request.id}`}>
                    <Button variant="outline" size="sm">
                      View Details
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}